'use client';

import { getGamePlays, getScoringSummary } from '@/data/content/liveStats';
import { getPlayersStatsForGame } from '@/data/content/stats';
import { mergeLiveRow, OtnChannels, OtnSocketMessageData, readPlayerRow, readTeamRow } from '@/helpers/sockets';
import { OtnGameOtnTeam } from '@models/ot_nationals/otn_game_otn_team';
import { OtnGameOtnTeamOtnPlayer } from '@models/ot_nationals/otn_game_otn_team_otn_player';
import {
	OtnDrive,
	OtnDriveOutline,
	OtnGameSituation,
	OtnPublicPlay,
	OtnScoringSummaryEntry,
} from '@models/ot_nationals/otn_live_stats';
import { useEffect, useRef, useState } from 'react';
import { useWebSocketContext } from '../contexts/WebsocketContext';

// Live-stats hooks (TECHENG-733). Each one only subscribes when its `enabled` switch is on,
// so a game without display_settings behaves exactly as it did before live stats existed.
// State is seeded from the server snapshot once; callers key their component by game id so a
// navigation to another game remounts with that game's snapshot.

/** The channel may carry the situation bare or wrapped as { situation }. */
const readSituation = (data: OtnSocketMessageData, gameId: string): OtnGameSituation | null => {
	const candidate = (data.situation ?? data) as Partial<OtnGameSituation>;
	if (!candidate || candidate.otn_game_id !== gameId || candidate.stats_mode === undefined) return null;
	return candidate as OtnGameSituation;
};

export const useLiveGameSituation = (
	gameId: string,
	initial: OtnGameSituation | null,
	enabled: boolean,
): OtnGameSituation | null => {
	const { subscribeToChannel } = useWebSocketContext();
	const [situation, setSituation] = useState<OtnGameSituation | null>(initial);

	useEffect(() => {
		if (!enabled || !gameId) return;
		return subscribeToChannel(OtnChannels.SITUATION, gameId, (data: OtnSocketMessageData) => {
			const live = readSituation(data, gameId);
			if (live) setSituation(live);
		});
	}, [gameId, enabled, subscribeToChannel]);

	return situation;
};

const outlineOf = (drives: OtnDrive[]): OtnDriveOutline[] =>
	drives.map((d) => ({
		offense_otn_team_id: d.offense_otn_team_id,
		start_period: d.start_period,
		result: d.result ?? null,
		play_ids: d.plays.map((p) => p.id),
	}));

type LivePlays = {
	/** Flat and ordered by play_order. */
	plays: OtnPublicPlay[];
	/** The server's drive breakdown, or null when the API sent plays without one (older payloads). */
	outline: OtnDriveOutline[] | null;
};

/**
 * The play list and the server's drive breakdown, kept current from the plays channel. Each
 * message carries the changed plays plus the whole game's outline, so the drives always match a
 * fresh load (TECHENG-1076). If the outline names a play this page never received (a missed
 * message), the drives are re-fetched once.
 */
export const useLivePlays = (
	gameId: string,
	initialDrives: OtnDrive[],
	enabled: boolean,
	preview = false,
): LivePlays => {
	const { subscribeToChannel } = useWebSocketContext();
	const [live, setLive] = useState<LivePlays>(() => ({
		plays: initialDrives.flatMap((d) => d.plays).sort((a, b) => a.play_order - b.play_order),
		outline: outlineOf(initialDrives),
	}));

	useEffect(() => {
		if (!enabled || !gameId) return;
		let cancelled = false;
		let refetching = false;
		const refetch = () => {
			if (refetching) return;
			refetching = true;
			getGamePlays(gameId, preview).then((drives) => {
				refetching = false;
				if (cancelled || !drives) return;
				setLive({
					plays: drives.flatMap((d) => d.plays).sort((a, b) => a.play_order - b.play_order),
					outline: outlineOf(drives),
				});
			});
		};

		const unsubscribe = subscribeToChannel(OtnChannels.PLAYS, gameId, (data: OtnSocketMessageData) => {
			if (data.otn_game_id && data.otn_game_id !== gameId) return;
			const incoming = data.plays ?? [];
			const deleted = new Set(data.deleted_play_ids ?? []);
			if (!incoming.length && !deleted.size && !data.drives) return;

			setLive((prev) => {
				const byId = new Map(prev.plays.filter((p) => !deleted.has(p.id)).map((p) => [p.id, p]));
				incoming.forEach((p) => byId.set(p.id, p));
				const plays = [...byId.values()].sort((a, b) => a.play_order - b.play_order);
				const outline = data.drives ?? null;
				if (outline?.some((d) => d.play_ids.some((id) => !byId.has(id)))) queueMicrotask(refetch);
				return { plays, outline };
			});
		});
		return () => {
			cancelled = true;
			unsubscribe();
		};
	}, [gameId, enabled, preview, subscribeToChannel]);

	return live;
};

/** Scoring summary, re-fetched whenever a play arrives that scored or was removed. */
export const useLiveScoringSummary = (
	gameId: string,
	initial: OtnScoringSummaryEntry[],
	enabled: boolean,
	preview = false,
): OtnScoringSummaryEntry[] => {
	const { subscribeToChannel } = useWebSocketContext();
	const [scores, setScores] = useState<OtnScoringSummaryEntry[]>(initial);

	useEffect(() => {
		if (!enabled || !gameId) return;
		let cancelled = false;
		const unsubscribe = subscribeToChannel(OtnChannels.PLAYS, gameId, (data: OtnSocketMessageData) => {
			if (data.otn_game_id && data.otn_game_id !== gameId) return;
			const touchesScoring = (data.plays ?? []).some((p) => p.points > 0) || (data.deleted_play_ids ?? []).length > 0;
			if (!touchesScoring) return;
			getScoringSummary(gameId, preview).then((next) => {
				if (!cancelled && next) setScores(next);
			});
		});
		return () => {
			cancelled = true;
			unsubscribe();
		};
	}, [gameId, enabled, preview, subscribeToChannel]);

	return scores;
};

type LiveBoxStats = {
	teams: OtnGameOtnTeam[];
	players: OtnGameOtnTeamOtnPlayer[];
};

/**
 * Team and player stat rows kept current from the existing team and player channels. A
 * socket row carries no relations, so the player/team objects from the snapshot are kept;
 * a player that wasn't in the snapshot triggers one re-fetch to pick up name and jersey.
 */
export const useLiveBoxStats = (
	gameId: string,
	initialTeams: OtnGameOtnTeam[],
	initialPlayers: OtnGameOtnTeamOtnPlayer[],
	enabled: boolean,
	preview = false,
): LiveBoxStats => {
	const { subscribeToChannel } = useWebSocketContext();
	const [stats, setStats] = useState<LiveBoxStats>({ teams: initialTeams, players: initialPlayers });
	const knownPlayerRowIds = useRef(new Set(initialPlayers.map((p) => p.id)));

	useEffect(() => {
		if (!enabled || !gameId) return;
		let cancelled = false;

		const unsubscribeTeams = subscribeToChannel(OtnChannels.TEAMS, gameId, (data: OtnSocketMessageData) => {
			const row = readTeamRow(data, gameId);
			if (!row) return;
			setStats((prev) => ({
				...prev,
				teams: prev.teams.map((t) => (t.otn_team_id === row.otn_team_id ? mergeLiveRow(t, row) : t)),
			}));
		});

		const unsubscribePlayers = subscribeToChannel(OtnChannels.PLAYERS, gameId, (data: OtnSocketMessageData) => {
			const row = readPlayerRow(data, gameId);
			if (!row) return;
			const isNew = !knownPlayerRowIds.current.has(row.id);
			knownPlayerRowIds.current.add(row.id);
			setStats((prev) => {
				const idx = prev.players.findIndex((p) => p.id === row.id);
				if (idx === -1) return { ...prev, players: [...prev.players, row] };
				const players = [...prev.players];
				const existing = players[idx];
				players[idx] = mergeLiveRow(existing, row);
				return { ...prev, players };
			});
			if (isNew && !row.otn_player) {
				getPlayersStatsForGame(gameId, { preview }).then((fresh) => {
					if (cancelled || !fresh.length) return;
					fresh.forEach((p) => knownPlayerRowIds.current.add(p.id));
					setStats((prev) => ({ ...prev, players: fresh }));
				});
			}
		});

		return () => {
			cancelled = true;
			unsubscribeTeams();
			unsubscribePlayers();
		};
	}, [gameId, enabled, preview, subscribeToChannel]);

	return stats;
};
